import React, { useState, useEffect, RefObject } from 'react';
import { SiteId } from '../components/Product/ProductBadge';

interface MediaItem {
  type: string;
  url: string;
  thumbUrl?: string;
}

interface IframeDimensions {
  widthPercentage: number;
  paddingTopPercentage: number;
}

const useGalleryState = (thumbnailListRef: RefObject<HTMLDivElement>) => {
  const [loading, setLoading] = useState(true);
  const galleryInjectionPoint = document.querySelector('.react-media-gallery');
  const [selectedVariantData, setSelectedVariantData] = useState(null);
  const [allVariantsData, setAllVariantsData] = useState(null);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const videoIndex = mediaItems.findIndex(item => item.type === 'video');

  const [marketingVideoPreview, setMarketingVideoPreview] = useState<string | null>(null);

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const [showUpArrow, setShowUpArrow] = useState(false);
  const [showDownArrow, setShowDownArrow] = useState(true);

  const siteIdValue: string | null = galleryInjectionPoint
    ? galleryInjectionPoint.getAttribute('data-site-brand')
    : null;
  const siteId: SiteId =
    siteIdValue == 'fender' || siteIdValue == 'jackson' || siteIdValue == 'presonus' ? siteIdValue : 'presonus';

  const galleryType = galleryInjectionPoint?.getAttribute('data-gallery-type');

  const [modalSelectedImageIndex, setModalSelectedImageIndex] = useState(selectedImageIndex);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalImageMaxHeight, setModalImageMaxHeight] = useState(window.innerHeight);

  const [showThreeSixtyToggle, setShowThreeSixtyToggle] = useState<boolean | null>(null);
  const [showThreeSixtyOverlay, setShowThreeSixtyOverlay] = useState(false);
  const [iframeDimensions, setIframeDimensions] = useState<IframeDimensions>({
    widthPercentage: 100,
    paddingTopPercentage: 56.25
  });

  const [selectedVariantId, setSelectedVariantId] = useState(selectedVariantData?.id);
  const [url, setUrl] = useState(window.location.search);

  const openModalAtIndex = (index: number) => {
    setSelectedImageIndex(index);
    setModalSelectedImageIndex(index);
    setModalVisible(true);
  };

  useEffect(() => {
    function decodeHTMLEntities(text: string | null | undefined) {
      const textArea = document.createElement('textarea');
      textArea.innerHTML = text ? text : '';
      return textArea.value;
    }

    const reactElement = document.getElementById('react')
      ? document.getElementById('react')
      : document.getElementById('fender-react');
    const encodedSelectedVariantData = reactElement?.closest('media-gallery')?.getAttribute('data-selected-variant');
    const encodedAllVariantsData = reactElement?.closest('media-gallery')?.getAttribute('data-all-variants');

    const decodedSelectedVariantData = decodeHTMLEntities(encodedSelectedVariantData);
    setSelectedVariantData(decodedSelectedVariantData ? JSON.parse(decodedSelectedVariantData) : null);

    const decodedAllVariantsData = decodeHTMLEntities(encodedAllVariantsData);
    setAllVariantsData(decodedAllVariantsData ? JSON.parse(decodedAllVariantsData) : null);

    setLoading(false); // Set loading to false once data is processed
  }, []);

  useEffect(() => {
    if (selectedVariantData) {
      const variantMedia = selectedVariantData.variantMedia;
      let newMediaItems: MediaItem[] = [];

      if (variantMedia.images) {
        newMediaItems = variantMedia.images.map((image: { url: string; altText: string }) => ({
          type: 'image',
          url: image.url,
          thumbUrl: image.url
        }));
      }
      if (variantMedia.videos) {
        variantMedia.videos.forEach((video: { id: string; type: string; altText: string; url?: string }) => {
          if (video.type === 'youtube') {
            const videoItem = {
              type: 'video',
              url: `https://www.youtube.com/embed/${video.id}`,
              thumbUrl: `https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`
            };
            if (newMediaItems.length > 0) {
              newMediaItems.splice(1, 0, videoItem);
            } else {
              newMediaItems.push(videoItem);
            }
          }
          if (video.type === 'mp4' && video.url) {
            setMarketingVideoPreview(video.url);
          }
        });
      }
      setMediaItems(newMediaItems);
    }
  }, [selectedVariantData]);

  useEffect(() => {
    const containerWidth = window.innerWidth;
    const newDimensions = calculateIframeDimensions(containerWidth, modalImageMaxHeight);
    setIframeDimensions(newDimensions);
  }, [modalImageMaxHeight]);

  useEffect(() => {
    const handleUrlChange = () => {
      setUrl(window.location.search);
    };

    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('pushState', handleUrlChange);
    window.addEventListener('replaceState', handleUrlChange);

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('pushState', handleUrlChange);
      window.removeEventListener('replaceState', handleUrlChange);
    };
  }, []);

  useEffect(() => {
    if (url.includes('variant=')) {
      console.log(url);
      const urlParams = new URLSearchParams(url);
      const variantId = urlParams.get('variant');
      if (variantId) {
        setSelectedVariantId(variantId);
      }
    }
    console.log('selectedVariantId', selectedVariantId);
  }, [url]);

  useEffect(() => {
    if (selectedVariantId && allVariantsData) {
      console.log(allVariantsData);
      allVariantsData.filter(variant => {
        if (variant.id === selectedVariantId) {
          setSelectedVariantData(variant.media);
        }
      });
    }
  }, [selectedVariantId]);

  const toggleShow360View = () => {
    setShowThreeSixtyOverlay(!showThreeSixtyOverlay);
  };

  const calculateIframeDimensions = (containerWidth: number, modalImageMaxHeight: number): IframeDimensions => {
    let calculatedWidth = modalImageMaxHeight * (16 / 9);
    let widthPercentage = (calculatedWidth / containerWidth) * 100;
    widthPercentage = Math.min(widthPercentage, 100);

    let paddingTopPercentage;

    if (widthPercentage < 26.16) {
      widthPercentage = 26.15;
      paddingTopPercentage = 14.71;
    } else {
      const originalPaddingTop = 56.25;
      paddingTopPercentage = originalPaddingTop * (widthPercentage / 100);
    }

    return { widthPercentage, paddingTopPercentage };
  };

  const handleGalleryClick = () => {
    setModalVisible(!modalVisible);
    if (!modalVisible) {
      setModalSelectedImageIndex(selectedImageIndex);
    }
  };

  const handleGalleryScroll = (direction: 'left' | 'right') => {
    let newIndex = selectedImageIndex;
    if (direction === 'left') {
      newIndex = (selectedImageIndex - 1 + mediaItems.length) % mediaItems.length;
    } else if (direction === 'right') {
      newIndex = (selectedImageIndex + 1) % mediaItems.length;
    }
    if (showThreeSixtyOverlay) {
      setShowThreeSixtyOverlay(!showThreeSixtyOverlay);
    }
    setSelectedImageIndex(newIndex);
  };

  const handleModalGalleryScroll = (direction: 'left' | 'right') => {
    let newIndex = modalSelectedImageIndex;
    const totalItems = mediaItems.length;

    if (direction === 'left') {
      newIndex = (modalSelectedImageIndex - 1 + totalItems) % totalItems;
    } else if (direction === 'right') {
      newIndex = (modalSelectedImageIndex + 1) % totalItems;
    }
    setModalSelectedImageIndex(newIndex);
  };

  const handleModalThumbnailClick = (index: number) => {
    setModalSelectedImageIndex(index);
  };

  const handleThumbnailClick = (index: number) => {
    setSelectedImageIndex(index);
    if (showThreeSixtyOverlay) {
      setShowThreeSixtyOverlay(!showThreeSixtyOverlay);
    }
  };

  const checkForScrollPosition = () => {
    if (thumbnailListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = thumbnailListRef.current;
      setShowUpArrow(scrollTop > 0);
      setShowDownArrow(scrollTop < scrollHeight - clientHeight);
    }
  };

  const handleThumbnailScroll = (direction: number) => {
    if (thumbnailListRef.current) {
      const { clientHeight } = thumbnailListRef.current;
      thumbnailListRef.current.scrollBy({
        top: (direction * clientHeight) / 3,
        behavior: 'smooth'
      });
      setTimeout(checkForScrollPosition, 200);
    }
  };

  useEffect(() => {
    if (!thumbnailListRef.current) return;
    checkForScrollPosition();
    window.addEventListener('resize', checkForScrollPosition);

    const handleScroll = () => {
      checkForScrollPosition();
    };

    const currentThumbnailList = thumbnailListRef.current;
    if (currentThumbnailList) {
      currentThumbnailList.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('resize', checkForScrollPosition);
      if (currentThumbnailList) {
        currentThumbnailList.removeEventListener('scroll', handleScroll);
      }
    };
  }, [thumbnailListRef.current]);

  const handleResize = () => {
    const newWidth = window.innerWidth;
    setScreenWidth(newWidth);
    const thumbnailHeight = thumbnailListRef.current ? thumbnailListRef.current.offsetHeight : 0;
    let availableHeight = window.innerHeight - thumbnailHeight - 200;
    availableHeight = Math.max(availableHeight, 20);

    setModalImageMaxHeight(availableHeight);

    const newDimensions = calculateIframeDimensions(newWidth, availableHeight);
    setIframeDimensions(newDimensions);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [modalVisible]);

  useEffect(() => {
    if (siteId === 'fender') return;

    const galleryWrapper = document.querySelector('.fender-product-main .product');
    if (galleryWrapper) {
      galleryWrapper.style.zIndex = modalVisible ? '9999' : '2';
    }

    return () => {
      if (galleryWrapper) {
        galleryWrapper.style.zIndex = '2';
      }
    };
  }, [modalVisible, siteId]);

  return {
    loading,
    selectedMediaIndex: selectedImageIndex,
    modalSelectedImageIndex,
    mediaItems,
    videoIndex,
    modalVisible,
    modalImageMaxHeight,
    iframeDimensions,
    showThreeSixtyToggle,
    showThreeSixtyOverlay,
    showUpArrow,
    showDownArrow,
    siteId,
    selectedVariantData,
    marketingVideoPreview,
    screenWidth,
    galleryType,
    toggleShow360View,
    handleThumbnailClick,
    handleGalleryScroll,
    handleThumbnailScroll,
    handleGalleryClick,
    handleModalThumbnailClick,
    handleModalGalleryScroll,
    openModalAtIndex
  };
};

export default useGalleryState;
